/**
 * Bearer-token session persistence for the standalone (home-screen) PWA.
 *
 * The primary session transport is the HttpOnly cookie set by the
 * /auth/* endpoints — but iOS gives the home-screen PWA a cookie jar
 * that is separate from Safari and does not reliably persist, so cookie
 * sessions silently vanish inside the installed app. The backend
 * therefore also returns the session JWT in response bodies
 * (POST /auth/verify-code, GET /auth/me on sliding renewal, and the
 * /auth/complete#token=… fragment after Google sign-in), and every API
 * endpoint accepts it back as `Authorization: Bearer <jwt>`.
 *
 * Storage policy (XSS blast radius): the token is written to
 * localStorage ONLY in standalone display mode, where the cookie is
 * unreliable. Desktop/tab browsing keeps relying on the HttpOnly
 * cookie, which JS can't read — persisting the JWT there would widen
 * XSS exposure for no benefit. The Authorization header, however, is
 * attached whenever a stored token exists (api.ts) — standalone-ness
 * gates the WRITE, not the read.
 */

export const SESSION_TOKEN_STORAGE_KEY = 'dregsbane_session_token';

/**
 * True when running as an installed app rather than a browser tab —
 * home-screen PWA or the native Capacitor shell. Both have the unreliable
 * cookie jar this token exists to work around.
 *
 * Three independent signals, because no one of them covers every case:
 *
 * - `display-mode: standalone` — the standard home-screen-PWA signal.
 * - `navigator.standalone` — the legacy iOS Safari flag.
 * - `capacitor:` protocol / `window.Capacitor` — the native shell.
 *
 * The Capacitor check is NOT redundant. Measured in the iOS simulator on
 * 2026-08-05, a Capacitor WKWebView reports `display-mode: standalone` =
 * false AND `navigator.standalone` = false, so without it `isStandalone()`
 * returned false inside the shell — and since this function gates every
 * `storeToken()` call site (api.ts sign-in + renewal, auth-complete.tsx),
 * the shell signed users in and then silently dropped the session on the
 * next launch. That is the exact failure bearer persistence was added to
 * fix; an earlier comment here asserted `navigator.standalone` was true
 * under Capacitor, and it is not.
 */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches
  ) {
    return true;
  }
  if ((window.navigator as Navigator & { standalone?: boolean }).standalone === true) {
    return true;
  }
  return isNativeShell();
}

/**
 * True inside the Capacitor native shell.
 *
 * Checks the origin scheme first: it is set by the shell's own config and
 * is true from the first line of script, before the Capacitor bridge
 * global has necessarily been injected. `window.Capacitor` is the fallback
 * so this keeps working if the scheme is ever reconfigured.
 *
 * Lives here rather than in lib/native-auth.ts because isStandalone()
 * needs it and this module is in the entry chunk — importing it the other
 * way round would drag the OAuth launcher into first paint.
 */
export function isNativeShell(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.location?.protocol === 'capacitor:') return true;
  return (window as Window & { Capacitor?: unknown }).Capacitor != null;
}

export function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(SESSION_TOKEN_STORAGE_KEY);
  } catch {
    return null; // privacy mode — cookie path still works
  }
}

/**
 * Raw write — callers enforce the standalone-only storage policy (see
 * module doc): new sessions store via `isStandalone()`; sliding renewal
 * replaces a token that is already stored.
 */
export function storeToken(token: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SESSION_TOKEN_STORAGE_KEY, token);
  } catch {
    /* storage full / privacy mode — bearer persistence is best-effort */
  }
}

export function clearToken(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
  } catch {
    /* nothing to clear if storage is unavailable */
  }
}
